import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import recruitServiceV1 from "../../../api/renewal/recruit.service.v1";
import FaqFooter from "../../../components/templates/faqFooter";
import MainSection from "../../../components/templates/mainSection";
import MiddleNavBar from "../../../components/templates/middleNavBar";
import useDisplay from "../../../hooks/useDisplay";
import { mainBannerLang } from "../../../lang/lang";
import { langState } from "../../../store/langState";
import { FilterSideBar, IntroText, RecruitList } from "./_component";
import { SkeletonLoader } from "./_component/new-job-announce/recruitList";

export default function NewJobAnnounce() {
  const { isMobile, isTablet } = useDisplay();
  const isSmall = isMobile || isTablet;
  const langValue = useRecoilValue(langState);

  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  // Extract all relevant params from URL to drive state and API calls
  const searchParams = new URLSearchParams(location.search);
  const keyword = searchParams.get("keyword") || ""; // Default to empty string for consistency
  const careerLevel = searchParams.get("careerLevel") || "전체";
  const employmentType = searchParams.get("employmentType") || "전체";
  const periodType = searchParams.get("periodType") || "전체";

  const [recruitList, setRecruitList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const observerRef = useRef(null);
  const lastItemRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreData();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, hasMore]
  );

  // fetchRecruitList is memoized with useCallback to stabilize its reference for useEffect dependency
  const fetchRecruitList = useCallback(async (query, append = false) => {
    setIsLoading(true);
    try {
      const recruitListData = await recruitServiceV1.getRecruitList(query);

      if (append) {
        setRecruitList((prev) => [...prev, ...recruitListData.items]);
      } else {
        setRecruitList(recruitListData.items);
      }

      setHasMore(!recruitListData.last);
      return recruitListData;
    } catch (err) {
      console.log("err:", err);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array as setRecruitList is stable

  const loadMoreData = useCallback(() => {
    if (isLoading || !hasMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    const query = {
      page: nextPage,
      limit: 10,
      search: keyword || undefined,
      keyword: undefined,
      status: "모집 중",
      startDate: undefined,
      endDate: undefined,
      careerLevel: careerLevel === "전체" ? undefined : careerLevel,
      recruitmentType: periodType === "전체" ? undefined : periodType,
      sortBy: undefined,
      order: undefined,
    };

    fetchRecruitList(query, true);
  }, [
    currentPage,
    keyword,
    careerLevel,
    periodType,
    isLoading,
    hasMore,
    fetchRecruitList,
  ]);

  // Handlers to update URL when filters change
  const handleFilterChange = useCallback(
    (filterName, value) => {
      const currentParams = new URLSearchParams(location.search);
      const oldParamsString = currentParams.toString();

      if (value === "") {
        // No items selected for this category
        currentParams.delete(filterName);
      } else {
        // "전체" or specific items like "경력" or "상시,기간"
        currentParams.set(filterName, value);
      }

      const newParamsString = currentParams.toString();

      if (oldParamsString !== newParamsString) {
        navigate(`${pathname}?${newParamsString}`, { replace: true });
      }
    },
    [navigate, location.search, pathname]
  );

  const setCareerLevel = useCallback(
    (newVal) => {
      handleFilterChange("careerLevel", newVal);
    },
    [handleFilterChange]
  );

  const setPeriodType = useCallback(
    (newVal) => {
      handleFilterChange("periodType", newVal);
    },
    [handleFilterChange]
  );

  // Main data fetching effect: re-runs when URL-derived parameters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);

    const fetchData = async () => {
      const query = {
        page: 1,
        limit: 10,
        search: keyword || undefined, // Use keyword from URL
        keyword: undefined, // As per original: this specific 'keyword' field in query is undefined
        status: "모집 중",
        startDate: undefined,
        endDate: undefined,
        careerLevel: careerLevel === "전체" ? undefined : careerLevel, // Use careerLevel from URL
        recruitmentType: periodType === "전체" ? undefined : periodType, // Use periodType from URL
        sortBy: undefined,
        order: undefined,
      };

      await fetchRecruitList(query, false);
    };

    fetchData();
  }, [keyword, careerLevel, employmentType, periodType, fetchRecruitList]);

  return (
    <div className="w-full">
      <div id="tailwind-root">
        <MainSection
          bg={
            isMobile
              ? "/assets/imgs/main-section/m_job_announce.png"
              : "/assets/imgs/main-section/job_announce.png"
          }
          title={mainBannerLang.newJobAnnouncement.title[langValue]}
          content={mainBannerLang.newJobAnnouncement.content[langValue]}
        />
        <MiddleNavBar pathname={pathname} />
        <section
          className={`max-w-[1400px] mx-auto mt-[60px] ${
            isTablet ? "px-[70px]" : "px-[20px]"
          }`}
        >
          <IntroText
            isMobile={isMobile}
            title={"진행중인 채용 포지션"}
            subTitle={"서로 화합하고 조화를 이루는 곳, 루미르에 합류하세요!"}
          />
          <div
            className={`flex gap-40 mt-20 mb-[60px] min-h-[calc(100vh-200px)]`}
          >
            {!isSmall && (
              <FilterSideBar
                setCareerLevel={setCareerLevel}
                setPeriodType={setPeriodType}
              />
            )}
            <div className="w-full">
              <RecruitList recruitList={recruitList} isMobile={isMobile} />
              {recruitList.length > 0 && hasMore && (
                <div ref={lastItemRef} className="w-full py-4">
                  {isLoading && (
                    <div className="mt-8">
                      <SkeletonLoader count={3} isMobile={isMobile} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      <FaqFooter />
    </div>
  );
}
